// The tab «Письма» of a record's card (R-M6): the chains about this record — a contact, a company, a deal — newest first;
// a chain opens in the mail client, «Написать» starts a letter to the record's address. Below — the letters the system sent about it.

import { useMemo } from 'react';
import type { Id } from '@confdeck/core-shared';
import { navigate } from '../../shell/router.ts';
import { Letters } from '../../record/Letters.tsx';
import { useMeta, useQuery, useRecord } from '../../sync/react.tsx';
import { Icon } from '../../ui/icons.tsx';
import { Button, cx } from '../../ui/primitives.tsx';
import { listTime, senderOf, type Letter } from './model.ts';
import { useLetters } from './use-letters.ts';
import s from './mail.module.css';

export function RecordLetters({ recordId }: { recordId: Id }) {
  const letters = useLetters();
  const rec = useRecord(recordId);
  const meta = useMeta();
  const chains = useMemo(() => {
    const byThread = new Map<string, { last: Letter; count: number; unread: boolean }>();
    for (const l of letters) {
      if (!l.linked.includes(recordId) || l.folder === 'trash' || l.folder === 'drafts') continue;
      const c = byThread.get(l.thread);
      if (!c) byThread.set(l.thread, { last: l, count: 1, unread: l.unread });
      else { c.count++; c.unread ||= l.unread; }
    }
    return [...byThread.values()];
  }, [letters, recordId]);
  const service = meta.typeByKey('email_message');
  const byRecord = useMemo(() => ({ field: 'record', op: 'eq' as const, value: recordId }), [recordId]);
  const sent = useQuery(service?.id ?? '', byRecord, [], '');
  const email = rec ? meta.fields(rec.typeId).filter((f) => f.fieldType === 'email').map((f) => String(rec.data[f.key] ?? '')).find(Boolean) : '';

  return (
    <div className={s.tab}>
      <div className={s.tabBar}>
        <span className={s.summary}>{chains.length ? `Переписка: ${chains.length}` : 'Писем пока нет'}</span>
        <span className={s.spacer} />
        {email && <Button size="sm" icon="compose" onClick={() => navigate(`/s/mail?to=${encodeURIComponent(email)}`)}>Написать</Button>}
      </div>
      {chains.length === 0 ? (
        <p className={s.hint}>Письма появятся здесь, когда придут с адреса {email || 'записи'} или будут отправлены на него.</p>
      ) : (
        <ul className={s.chains}>
          {chains.map(({ last, count, unread }) => (
            <li key={last.thread}>
              <a className={cx(s.row, s.chainRow)} data-unread={unread || undefined} href={`/s/mail?letter=${last.id}`}
                onClick={(e) => { if (e.metaKey || e.ctrlKey) return; e.preventDefault(); navigate(`/s/mail?letter=${last.id}`); }}>
                <div className={s.rowBody}>
                  <div className={s.line}>
                    <span className={s.sender}>{senderOf(last)}</span>
                    {count > 1 && <span className={s.tag}>{count}</span>}
                    <time className={s.time} dateTime={last.date}>{listTime(last.date)}</time>
                  </div>
                  <div className={s.line}>
                    <span className={s.subject}>{last.subject || '(без темы)'}</span>
                    {last.attachments.length > 0 && <span className={s.mark}><Icon name="paperclip" size={14} /></span>}
                  </div>
                  <div className={s.line}><span className={s.preview}>{last.snippet}</span></div>
                </div>
              </a>
            </li>
          ))}
        </ul>
      )}
      {service && sent.ids.length > 0 && <>
        <div className={s.tabBar}><span className={s.summary}>Служебные письма системы: {sent.ids.length}</span></div>
        <Letters recordId={recordId} onOpen={(id) => navigate(`/r/${id}`)} />
      </>}
    </div>
  );
}
